'use client';

import React, { useContext, useEffect, useRef, useState } from 'react';
import {
  type ProductItem,
  type ProductMedia,
  type ProductVariants,
  type Variant,
} from '@/common/types/product-details.type';
import {
  type Combinations,
  type FeatureType,
  type ProductdiversityType,
} from '@/common/types/product-features.type';
import { useRouter } from 'nextjs-toploader/app';
import { isDesktop } from 'react-device-detect';
import { useGetProductAttributeById } from '@/common/apis/attributes/get-product-attribute-by-id';
import { type VendorItemResponse } from '@/common/apis/storefronts/v1/get-vendor.api';
import ImageCarousel from '@/common/components/image-carousel';
import { ResponsiveWrapper } from '@/common/components/wrapper';
import { appConfigs } from '@/common/configs/app.config';
import { FeatureFlag } from '@/common/constants/feature-flag.enum';
import { ProductContext } from '@/common/contexts/product.context';
import { useFeatureFlag } from '@/common/hooks/use-feature-flag.hook';
import { Cartesian } from '@/common/utils/cartesian.utility';
import { createQueryStringUtility } from '@/common/utils/create-query-string.utility';
import { rialToToman } from '@/common/utils/money-exchange.utility';
import Buttons from './buttons.component';
import Description from './description.component';
import FeaturesPreview from './feature.component';
import { ProductAttributesBoxSection } from './product-attributes-box-section.component';
import { ProductAttributesCollapseSection } from './product-attributes-collapse-section.component';
import ProductDetail from './product-detail.component';
import ShippingPrice from './shipping-price.component';

interface Props {
  productMedia: ProductMedia;
  productDetails: Partial<ProductItem>;
  productVariants?: ProductVariants[] | null;
  variants?: Variant[];
  price: number;
  discount: number;
  description?: string;
  isPending: boolean;
  addToCart: () => void;
  setVariant: (variant: ProductVariants | undefined) => void;
  vendor?: VendorItemResponse;
}

interface ImageType {
  name?: string;
  url: string;
  uid?: string;
}
const DESCRIPTION_COLLAPSE_THRESHOLD = 400;
// eslint-disable-next-line max-lines-per-function
const Header = ({
  productMedia,
  productDetails,
  productVariants,
  variants,
  price,
  discount,
  description,
  isPending,
  addToCart,
  setVariant,
  vendor,
}: Props) => {
  const router = useRouter();
  const shippingMethodsEnabled = useFeatureFlag(FeatureFlag.ShippingMethods);
  const { setSelectedImageIndex, selectedImageIndex } =
    useContext(ProductContext);

  const { data: productAttributes } = useGetProductAttributeById({
    id: Number(productDetails.id) || 0,
  });

  const [combinations, setCombinations] = useState<Combinations[]>([]);
  const [variationPrice, setVariationPrice] = useState(price);
  const ref = useRef<HTMLDivElement>(null);
  const images: ImageType[] = [
    productMedia?.thumbnailImageProduct || [{ url: '', name: '', uid: '' }],
    ...(productMedia?.productImagesProduct || [{ id: 0, url: '' }]),
  ];
  const features = React.useMemo(
    () =>
      variants?.reduce((acc: string[], curr: Variant) => {
        if (!acc.includes(curr.title)) {
          acc.push(curr.title);
        }
        return acc;
      }, []),
    [variants]
  );

  const openGalleryHandler = () => {
    router.push(`${createQueryStringUtility({ state: 'gallery' })}`);
  };

  const diversityArray = React.useMemo(
    () =>
      variants?.reduce<FeatureType[]>((acc, curr: Variant) => {
        if (
          !acc.find(
            (item) => item.type === curr.title && item.value === curr.value
          )
        ) {
          acc.push({ type: curr.title, value: curr.value });
        }
        return acc;
      }, []),
    [variants]
  );

  const diversity = React.useMemo(
    () =>
      features?.reduce<ProductdiversityType>((acc, feature) => {
        acc[feature] = diversityArray?.filter((item) => item.type === feature);
        return acc;
      }, {}),
    [diversityArray, features]
  );
  const createCartesian = () => {
    let adaptedDiversity;
    if (diversity) {
      adaptedDiversity = Object.keys(diversity).reduce(
        (acc: FeatureType[][], cur: string) => {
          if (diversity[cur]?.length) {
            acc.push(diversity[cur] as FeatureType[]);
          }
          return acc;
        },
        []
      );
    }
    const props = {
      baseArray: adaptedDiversity || [],
      price: [],
      inStock: [],
      discount,
      mainPrice: price,
    };
    const availableCombinations = Cartesian(props).map((combination) => {
      const variantIds = (features ?? []).map(
        (feature) =>
          variants?.find(
            (item) =>
              item.title === feature && item.value === combination[feature]
          )?.id
      );
      const matchingVariant = variantIds.some((id) => id === undefined)
        ? undefined
        : productVariants?.find(
            (item) =>
              item.variantId.length === variantIds.length &&
              item.variantId.every((id, index) => id === variantIds[index])
          );

      return {
        ...combination,
        price: matchingVariant?.productVariantPrice ?? combination.price,
        inStock: matchingVariant?.isAvailable ?? false,
      };
    });

    setCombinations(availableCombinations);
  };

  const setSelectedVariant = React.useCallback(
    (foundObject: Combinations | undefined, features: string[]) => {
      if (foundObject) {
        setVariationPrice(
          rialToToman(foundObject.price, appConfigs.rialExchangeRate)
        );
      } else {
        setVariationPrice(price);
      }
      const variantId: number[] = [];
      if (foundObject) {
        for (const property of features) {
          if (property in foundObject) {
            const variantObj = variants?.find(
              (item) =>
                item.title === property && item.value === foundObject[property]
            );
            variantObj && variantId.push(variantObj.id);
          }
        }
      }
      const finalSelectedVariant = productVariants?.find((item) => {
        if (item.variantId.length !== variantId.length) {
          return false;
        }
        return item.variantId.every(
          (element, index) => element === variantId[index]
        );
      });
      setVariant(finalSelectedVariant || undefined);
    },
    [price, productVariants, setVariant, variants]
  );

  useEffect(() => {
    if (productVariants) {
      createCartesian();
    }
  }, [price, productVariants, variants, discount, features, diversity]);

  return (
    <ResponsiveWrapper className="mt-8">
      <div
        ref={ref}
        className="relative flex w-full flex-col gap-3 overflow-hidden rounded-lg px-4 lg:flex-row lg:gap-10 lg:px-0"
      >
        <div className="">
          <ImageCarousel
            imageClassName="rounded-[6px] lg:!rounded-[8px]  aspect-square object-contain"
            images={images}
            discount={0}
            onOpenGallery={openGalleryHandler}
            productDetail={productDetails}
            outOfStock={productDetails.availability === 'OutOfStock'}
            onSelectImage={setSelectedImageIndex}
            hasThumbs={isDesktop}
            thumbClassName="rounded-[10px] aspect-square object-contain"
            selectedImage={selectedImageIndex}
          />
        </div>
        <article className="w-full max-w-[404px]">
          <Description
            vendor={vendor}
            title={productDetails.name || ''}
            categeory={productDetails.categoryName || ''}
            price={variationPrice || 0}
            discount={discount || 0}
          />
          {!shippingMethodsEnabled &&
          !isDesktop &&
          productDetails.shippingFeeAmount === 0 ? (
            <div className="mb-3">
              <span className="rounded-[4px] bg-coconut-default px-2 py-0.5 text-2xs font-medium text-secondary-400">
                ارسال رایگان
              </span>
            </div>
          ) : null}
          {combinations && diversity && features && (
            <FeaturesPreview
              combination={combinations}
              diversity={diversity}
              features={features}
              setter={setSelectedVariant}
            />
          )}
          {productAttributes && productAttributes.data.length > 0 && (
            <>
              <ProductAttributesBoxSection data={productAttributes.data} />
              <div className="flex justify-center gap-2 items-center w-full mt-6">
                <span className="w-full h-[1px] bg-gray-60 grow shrink" />
                <button
                  type="button"
                  onClick={() => {
                    document
                      .getElementById('product-attributes-collapse-section')
                      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }}
                  className="bg-transparent shrink-0 text-nowrap text-xs p-[10px] text-black-40 border border-gray-60 rounded-md"
                >
                  مشاهده همه ویژگی‌ها
                </button>
                <span className="w-full h-[1px] bg-gray-60 grow shrink" />
              </div>
            </>
          )}
          <div className="flex w-full flex-col-reverse lg:flex-col">
            <Buttons
              isOrderBlocked={vendor?.isOrderBlocked}
              isPending={isPending}
              addToCart={addToCart}
              availability={productDetails?.availability}
            />
            <div className="flex flex-col gap-5">
              {isDesktop && !shippingMethodsEnabled ? (
                <ShippingPrice
                  price={productDetails.shippingFeeAmount || 0}
                  payer={
                    productDetails.shippingFeeAmount === -1
                      ? 'shopper'
                      : 'vendor'
                  }
                />
              ) : null}

              {isDesktop && description && (
                <ProductDetail
                  description={description}
                  isCollapsable={
                    description.length > DESCRIPTION_COLLAPSE_THRESHOLD
                  }
                />
              )}
            </div>

            {productAttributes && productAttributes.data.length > 0 && (
              <ProductAttributesCollapseSection data={productAttributes.data} />
            )}
          </div>
        </article>
      </div>
    </ResponsiveWrapper>
  );
};

export default Header;
